/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Render the roster with guardian state, selection bar and row menu.
 * @state-owner classroom UI store owns selection; dataset store owns roster and guardians
 * @allowed-imports React; dataset and workspace stores; classroom store; shared components and hooks
 * @forbidden direct API client; student-only surfaces
 * @public-api ClassDetail
 * @side-effects dialogs, context menus and navigation
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { useActiveContextId } from '../../../app/providers/ActiveRoleProvider';
import { MenuRows } from '../../../shared/components/ContextMenu';
import { pointerPoint, useOverlay } from '../../../shared/components/OverlayHost';
import { NameCell, StatusText } from '../../../shared/components/StatusIcon';
import { CheckCell, tableCells, VTable } from '../../../shared/components/VTable';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { hasHttpStatus } from '../../../shared/lib/api-error';
import {
  classMaterials,
  classRuns,
  findSeat,
  seatPresentation,
  seatStatusLabel,
} from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { AssignmentDialog } from '../../activities';
import { useClassStudentsFiltered } from '../api/classrooms.queries';
import { GUARDIAN_STATE_LABEL, guardianState, guardianStats } from '../model/guardian';
import { useClassroomUiStore } from '../store/classroom-ui-store';
import { ClassroomMemberManagement } from './ClassroomMemberManagement';
import { GuardianBatchDialog } from './GuardianBatchDialog';
import { GuardianDialog } from './GuardianDialog';
import { OrganizationCredentialDialog } from './OrganizationCredentialDialog';
import './classroom-student-patrol.css';

export function restoreRosterPosition(
  surface: 'desktop' | 'mobile',
  scrollTop: number,
  rowId: string,
  root: ParentNode = document,
) {
  const container = root.querySelector<HTMLElement>(`[data-classroom-roster-scroll="${surface}"]`);
  root
    .querySelector<HTMLElement>(`[data-student-row-id="${rowId}"]`)
    ?.focus({ preventScroll: true });
  if (container) {
    container.scrollTop = scrollTop;
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        container.scrollTop = scrollTop;
      });
    }
    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        container.scrollTop = scrollTop;
      }, 0);
    }
  }
}

export function ClassStudentsTab({ classIndex }: { classIndex: number }) {
  const t = useTranslator();
  const contextId = useActiveContextId();
  const environment = useWorkspaceEnvironment();
  const { openModal, openPopover } = useOverlay();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);
  const seats = useDatasetStore((state) => state.seats);
  const guardians = useDatasetStore((state) => state.guardians);
  const openItem = useWorkspaceUiStore((state) => state.openItem);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const selected = useClassroomUiStore((state) => state.selectedStudents);
  const search = useClassroomUiStore((state) => state.studentSearch);
  const sort = useClassroomUiStore((state) => state.studentSort);
  const setSearch = useClassroomUiStore((state) => state.setStudentSearch);
  const setSort = useClassroomUiStore((state) => state.setStudentSort);
  const setStudentSelection = useClassroomUiStore((state) => state.setStudentSelection);
  const captureRosterPosition = useClassroomUiStore((state) => state.captureRosterPosition);
  const savedDesktopScrollTop = useClassroomUiStore((state) => state.rosterScrollTop.desktop);
  const savedMobileScrollTop = useClassroomUiStore((state) => state.rosterScrollTop.mobile);
  const focusedRowId = useClassroomUiStore((state) => state.focusedStudentRowId);
  const toggleStudent = useClassroomUiStore((state) => state.toggleStudent);
  const clearStudents = useClassroomUiStore((state) => state.clearStudents);
  const pendingPointerPosition = useRef<{
    surface: 'desktop' | 'mobile';
    studentId: string;
    scrollTop: number;
  } | null>(null);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(max-width: 760px)');
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);

  const classroom = classes[classIndex];
  const classroomSummary = {
    id: classroom.id,
    name: classroom.name,
    subject: classroom.subtitle.split(' · ')[0] ?? '',
    studentCount: classroom.students.length,
    joinCode: classroom.code,
    students: [...classroom.students],
  };
  const members = useClassStudentsFiltered(contextId, classroomSummary);
  const rosterStudents = (
    members.data ??
    classroom.students.map((name, index) => ({
      id: `${classroom.id}-member-${index}`,
      userId: `${classroom.id}-student-${index}`,
      name,
      email: null,
      role: 'student' as const,
      status: 'active' as const,
      lastSeenAt: null,
    }))
  ).filter((member) => member.role === 'student');
  const students = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase();
    return [...rosterStudents]
      .filter((member) => member.name.toLocaleLowerCase().includes(normalizedSearch))
      .sort((left, right) => {
        const comparison = left.name.localeCompare(right.name, 'zh-Hant');
        return sort === 'name-desc' ? -comparison : comparison;
      });
  }, [rosterStudents, search, sort]);
  const studentNames = students.map((student) => student.name);
  const live = classRuns(runs, classIndex).some((run) => run.status === 'live');
  const stats = guardianStats(studentNames, guardians);
  const guardianProgress = (count: number) =>
    `${stats.total ? Math.round((count / stats.total) * 100) : 0}%`;
  const firstMaterial =
    classMaterials(activities, classIndex)[0] ??
    activities.find((activity) => activity.status === 'ready');

  useEffect(() => {
    if (!members.data || !focusedRowId) return;
    restoreRosterPosition(
      mobile ? 'mobile' : 'desktop',
      mobile ? savedMobileScrollTop : savedDesktopScrollTop,
      focusedRowId,
    );
  }, [focusedRowId, members.data, mobile, savedDesktopScrollTop, savedMobileScrollTop]);

  if (members.isLoading)
    return (
      <div data-classroom-surface={mobile ? 'mobile' : 'desktop'}>
        <VispoLoading label="正在載入學生名單" />
      </div>
    );
  if (members.isError) {
    if (hasHttpStatus(members.error, 403))
      return (
        <p data-classroom-surface={mobile ? 'mobile' : 'desktop'} role="alert">
          你目前沒有權限查看這個班級的學生。
        </p>
      );
    if (hasHttpStatus(members.error, 404))
      return (
        <p data-classroom-surface={mobile ? 'mobile' : 'desktop'} role="alert">
          找不到這個班級。
        </p>
      );
    return (
      <p data-classroom-surface={mobile ? 'mobile' : 'desktop'} role="alert">
        學生名單暫時無法使用，請稍後再試。
        <button type="button" onClick={() => void members.refetch()}>
          重試
        </button>
      </p>
    );
  }

  const openStudent = (student: (typeof students)[number], surface: 'desktop' | 'mobile') => {
    const pending = pendingPointerPosition.current;
    const scrollTop =
      pending?.surface === surface && pending.studentId === student.id
        ? pending.scrollTop
        : (document.querySelector<HTMLElement>(`[data-classroom-roster-scroll="${surface}"]`)
            ?.scrollTop ?? 0);
    pendingPointerPosition.current = null;
    setStudentSelection(student.userId, student.id);
    captureRosterPosition(surface, scrollTop, student.id);
    openItem({ k: 'stu', n: student.name });
  };

  if (mobile) {
    return (
      <>
        <ClassroomMemberManagement classroom={classroomSummary} />
        <section data-classroom-surface="mobile" className="student-patrol-mobile">
          <header>
            <h2>學生名單</h2>
            <span>{students.length} 人</span>
          </header>
          <div className="student-patrol-controls">
            <label>
              搜尋學生
              <input
                aria-label="搜尋學生"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <label>
              排序
              <select
                aria-label="排序"
                value={sort}
                onChange={(event) => setSort(event.target.value as 'name-asc' | 'name-desc')}
              >
                <option value="name-asc">姓名 A-Z</option>
                <option value="name-desc">姓名 Z-A</option>
              </select>
            </label>
          </div>
          <div data-classroom-roster-scroll="mobile" className="student-patrol-mobile__scroll">
            {students.length ? (
              students.map((student) => (
                <button
                  className="student-patrol-mobile__row"
                  data-student-row-id={student.id}
                  key={student.id}
                  type="button"
                  onClick={() => openStudent(student, 'mobile')}
                >
                  <NameCell icon={student.name[0]} text={student.name} />
                  <span>{student.role === 'student' ? '學生' : student.role}</span>
                </button>
              ))
            ) : (
              <p>這個班還沒有學生。</p>
            )}
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <ClassroomMemberManagement classroom={classroomSummary} />
      <fieldset className="student-patrol-controls">
        <label>
          搜尋學生
          <input value={search} onChange={(event) => setSearch(event.target.value)} />
        </label>
        <label>
          排序
          <select
            value={sort}
            onChange={(event) => setSort(event.target.value as 'name-asc' | 'name-desc')}
          >
            <option value="name-asc">姓名 A-Z</option>
            <option value="name-desc">姓名 Z-A</option>
          </select>
        </label>
      </fieldset>
      <button
        className="gsum"
        type="button"
        onClick={() => openModal(<GuardianBatchDialog classIndex={classIndex} />)}
      >
        <span className="gsl">
          <b>{t('家長綁定')}</b> {`${stats.linked} / ${stats.total}`}
        </span>
        <span className="gbar sm">
          <i style={{ width: guardianProgress(stats.linked) }} />
          <i className="pd" style={{ width: guardianProgress(stats.pending) }} />
        </span>
        {stats.none ? <span className="vmut">{`${stats.none} ${t('人還沒收到邀請')}`}</span> : null}
        <div className="sp" />
        <span className="lk">{t('一次處理')}</span>
      </button>

      {selected.length ? (
        <div className="vselbar">
          <b>{`已選 ${selected.length} 位`}</b>
          <button className="bt sm" type="button" onClick={clearStudents}>
            {t('取消')}
          </button>
          <button
            className="bt sm pri"
            type="button"
            onClick={() =>
              openModal(
                <AssignmentDialog
                  activityId={firstMaterial?.id ?? 'a1'}
                  classIndex={classIndex}
                  preselected={selected}
                />,
              )
            }
          >
            {t('指派給這些人')}
          </button>
        </div>
      ) : null}

      <div
        data-classroom-surface="desktop"
        data-classroom-roster-scroll="desktop"
        className="student-patrol-desktop"
      >
        <VTable
          empty={t('這個班還沒有學生')}
          columns={[
            { heading: '' },
            { heading: t('學生') },
            { heading: t('狀態') },
            { heading: t('家長') },
            { heading: t('紀錄') },
          ]}
          rows={students.map((student) => {
            const seat = findSeat(seats[classIndex] ?? [], student.name);
            const look = seat ? seatPresentation(seat.status) : null;
            const hot = Boolean(live && look?.needsTeacher);
            const glyph = live && look ? look.glyph : 'todo';
            const guardian = GUARDIAN_STATE_LABEL[guardianState(guardians[student.name] ?? [])];
            return {
              key: student.id,
              on: selected.includes(student.name),
              onSelect: () => {
                openStudent(student, 'desktop');
              },
              onContextMenu: (event) => {
                event.preventDefault();
                event.stopPropagation();
                const scrollTop =
                  document.querySelector<HTMLElement>('[data-classroom-roster-scroll="desktop"]')
                    ?.scrollTop ?? 0;
                captureRosterPosition('desktop', scrollTop, student.id);
                const point = pointerPoint(event);
                openPopover({
                  x: point.x,
                  y: point.y,
                  className: 'cmenu rmenu',
                  content: (
                    <MenuRows
                      items={[
                        {
                          label: t('開啟學生'),
                          shortcut: '↵',
                          onSelect: () => {
                            openStudent(student, 'desktop');
                          },
                        },
                        {
                          label: t('檢視學習報告'),
                          shortcut: 'R',
                          onSelect: () => navigate('rep', student.name),
                        },
                        'divider',
                        {
                          label: t('勾選'),
                          shortcut: 'X',
                          onSelect: () => toggleStudent(student.name),
                        },
                      ]}
                    />
                  ),
                });
              },
              cells: tableCells(
                <CheckCell
                  on={selected.includes(student.name)}
                  onToggle={() => toggleStudent(student.name)}
                />,
                // biome-ignore lint/a11y/noStaticElementInteractions: capture pointer position before the row receives focus
                <span
                  data-student-row-id={student.id}
                  tabIndex={-1}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    pendingPointerPosition.current = {
                      surface: 'desktop',
                      studentId: student.id,
                      scrollTop:
                        document.querySelector<HTMLElement>(
                          '[data-classroom-roster-scroll="desktop"]',
                        )?.scrollTop ?? 0,
                    };
                  }}
                >
                  <NameCell icon={student.name[0]} text={student.name} tone={hot ? 'e' : ''} />
                </span>,
                <StatusText
                  glyph={glyph}
                  label={live && seat ? seatStatusLabel(seat, t) : t('沒有進行中的活動')}
                  tone={live && look ? look.tone : ''}
                />,
                <button
                  className="gcell"
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openModal(<GuardianDialog student={student.name} />);
                  }}
                >
                  <StatusText glyph={guardian[1]} label={t(guardian[0])} tone={guardian[2]} />
                </button>,
                environment.demoMode ? (
                  <span className="vmut">{`${classRuns(runs, classIndex).length} 份`}</span>
                ) : (
                  <button
                    aria-label={t(`管理 ${student.name} 的機構帳號`)}
                    className="bt sm"
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      openModal(
                        <OrganizationCredentialDialog
                          classroomId={classroom.id}
                          memberUserId={student.userId}
                          studentName={student.name}
                        />,
                      );
                    }}
                  >
                    {t('機構帳號')}
                  </button>
                ),
              ),
            };
          })}
          template="26px minmax(0,1fr) 190px 150px 110px"
        />
      </div>
    </>
  );
}
